import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { RelativeTime } from '@/components/RelativeTime';
import { useConfirm } from '@/hooks/useConfirm';
import type { AppBinding, VpnClientDevice } from '../api';
import { platformLabel } from '../format';
import { useAppBindings, useDisconnectApp, useMyVpnDevices, useRevokeVpnDevice } from '../hooks';
import { AppDownloads } from '../components/AppDownloads';
import { Card } from '../components/Facts';

/**
 * The caller's VPN client devices, and the Witina app's runs connected as
 * them, in account settings (docs/VpnAccess.md, "Slice 6 design" and "Slice 9
 * design"), under the app's downloads.
 */
export function MyVpnDevices() {
  const { data: devices, isLoading, isError } = useMyVpnDevices();
  const revoke = useRevokeVpnDevice();
  const apps = useAppBindings();
  const disconnect = useDisconnectApp();
  const { confirm, confirmDialog } = useConfirm();

  const onDisconnect = async (app: AppBinding) => {
    const ok = await confirm({
      title: `Disconnect Witina VPN on ${app.name}?`,
      message: 'Its sessions end now. Using it again means opening the app and its link again.',
      isDanger: true,
      confirmText: 'Disconnect',
    });
    if (ok) disconnect.mutate(app.id);
  };

  const onRevoke = async (device: VpnClientDevice) => {
    const ok = await confirm({
      title: `Revoke ${device.name}?`,
      message: 'Its sessions end now, and it can no longer connect. Using the VPN on it again means registering it again.',
      isDanger: true,
      confirmText: 'Revoke',
    });
    if (ok) revoke.mutate(device.id);
  };

  return (
    <PageContainer width="medium">
      <PageHeader title="VPN devices" subtitle="The computers you connect to customer sites from" />
      <div className="mb-5">
        <AppDownloads />
      </div>
      <div className="mb-5">
        <Card title="Connected apps" description="The Witina VPN apps signed in as you right now. Disconnect any you don't recognise.">
          {apps.isLoading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : !apps.data?.length ? (
            <p className="text-xs text-muted-foreground">None right now.</p>
          ) : (
            <ul className="divide-y divide-border">
              {apps.data.map((app) => (
                <li key={app.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm truncate">{app.name}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {platformLabel(app.platform)}
                      </Badge>
                      <Badge variant={app.connected ? 'default' : 'secondary'} className="text-[10px]">
                        {app.connected ? 'Connected' : 'Away'}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {app.sessions.length === 0
                        ? 'No sites on'
                        : app.sessions.map((s) => s.regionName).join(', ')}
                      {app.boundAt ? (
                        <>
                          {' · '}since <RelativeTime value={app.boundAt} />
                        </>
                      ) : null}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" disabled={disconnect.isPending} onClick={() => void onDisconnect(app)}>
                    Disconnect
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
      <Card title="Your devices" description="Revoke anything you no longer use or don't recognise.">
        {isLoading ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : isError ? (
          <p className="text-xs text-muted-foreground">Couldn&rsquo;t load your devices.</p>
        ) : !devices?.length ? (
          <p className="text-xs text-muted-foreground">
            None yet. A device appears here when you sign in to the Witina VPN app on it.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {devices.map((device) => (
              <li key={device.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm truncate">{device.name}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {device.platform}
                    </Badge>
                    {device.revokedAt ? (
                      <Badge variant="secondary" className="text-[10px]">
                        Revoked
                      </Badge>
                    ) : null}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {device.appVersion ? `app ${device.appVersion} · ` : ''}
                    {device.lastSeenAt ? (
                      <>
                        last used <RelativeTime value={device.lastSeenAt} />
                      </>
                    ) : (
                      <>
                        added <RelativeTime value={device.createdAt} />
                      </>
                    )}
                  </div>
                </div>
                {device.revokedAt ? null : (
                  <Button size="sm" variant="ghost" disabled={revoke.isPending} onClick={() => void onRevoke(device)}>
                    Revoke
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
      {confirmDialog}
    </PageContainer>
  );
}
